import { EnvironmentInjector, Injectable, runInInjectionContext } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { COLLECTIONS_BD } from 'app/core/models/collections.model';
import { DbService } from 'app/core/services/db.service';
import firebase from 'firebase/compat/app';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserSign } from '../models/user.model';

/**
 * Servicios de autenticacion
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  /**
   * Informacion del usuario actual
   */
  public userData: any;

  profile$ = new BehaviorSubject<any>([]);
  /**
   * Indica si se esta autenticado o no
   */
  private user!: Observable<firebase.User | null>;

  /**
   * Constructor
   *
   * @param afAuth autenticacion con firebase
   * @param router enrutador
   * @param db conexion con BD
   */
  constructor(
    private afAuth: AngularFireAuth,
    private router: Router,
    private db: DbService,
    private injector: EnvironmentInjector,
  ) {
    runInInjectionContext(this.injector, () => {
      this.user = this.afAuth.authState;
      this.afAuth.onAuthStateChanged((user: any) => {
        if (user) {
          this.userData = user;
          this.db.findOne(COLLECTIONS_BD.admins, user.uid).subscribe((profile) => {
            this.userData = profile;
            this.profile$.next(profile);
          });
          localStorage.setItem('user', this.userData.uid);
        } else {
          localStorage.removeItem('user');
        }
      });
    });
  }


  /**
   * Devuelve la info del perfil de usuario
   *
   * @returns perfil del usuario
   */
  profile(): Observable<any> {
    return this.profile$.asObservable();
  }
  /**
   * Obtener el estado de autenticación
   */
  get authenticated(): boolean {
    return this.user != null; // True ó False
  }

  /**
   * Obtener el observador del usuario actual
   */
  get currentUser(): Observable<firebase.User | null> {
    return this.userData;
  }

  /**
   * Retorna true cuando el usuario esta autenticado
   * y el email esta verificado
   */
  get isLoggedIn(): boolean {
    const user = localStorage.getItem('user');
    return (user !== null) ? true : false;
  }

  /**
   * Verificar correo para nuevos usuarios
   */
  verifyEmail(): any {
    const user: any = firebase.auth().currentUser;
    user.sendEmailVerification().then(() => {
      this.router.navigate(['verify-email']);
    });
  }

  /**
   * Crea un usuario con correo y contraseña
   *
   * @param email correo electronico
   * @param pass contraseña del usuario
   */
  signUpWithEmail(email: string, pass: string, name: string): void {
    runInInjectionContext(this.injector, () => this.afAuth.createUserWithEmailAndPassword(email, pass)).then((resp: any) => {
      resp.user.displayName = name;
      this.setDataUser(resp.user);
      this.verifyEmail();
    });
  }
  /**
   * Login a la app con correo y contraseña
   *
   * @param email correo del usuario
   * @param pass contraseña del usuario
   */
  signInWithEmail(
    email?: string,
    pass?: string
  ): void {
    runInInjectionContext(this.injector, () => this.afAuth.signInWithEmailAndPassword('domicilios@gmail.com', 'Domicilios2021*')).then((result: any) => {
      localStorage.setItem('user', result.user.uid);
    });
  }

  /**
   * Autenticación con Facebook
   */
  authWithFacebook(): Promise<firebase.auth.UserCredential> {
    const provider: firebase.auth.FacebookAuthProvider = new firebase.auth.FacebookAuthProvider();
    provider.addScope('user_birthday');
    return runInInjectionContext(this.injector, () => this.afAuth.signInWithPopup(provider));
  }
  /**
   * Autenticación con Google
   */
  authWithGoogle(): void {
    const provider: firebase.auth.GoogleAuthProvider = new firebase.auth.GoogleAuthProvider();
    provider.addScope('https://www.googleapis.com/auth/contacts.readonly');
    runInInjectionContext(this.injector, () => this.afAuth.signInWithPopup(new firebase.auth.GoogleAuthProvider())).then((resp: any) => {
      const userData: UserSign = {
        uid: resp.user.uid,
        email: resp.user.email,
        displayName: resp.user.displayName,
        photoURL: resp.user.photoURL,
        emailVerified: resp.user.emailVerified,
      };
      this.updateDataUser(userData);
      this.router.navigate(['/home']);
    });
  }

  /**
   * Cerrar sesión
   */
  signOut(): Promise<void> {
    localStorage.removeItem('user');
    this.router.navigate(['/login']);
    return runInInjectionContext(this.injector, () => this.afAuth.signOut());
  }

  /*
   * Recuperar contraseña
   *
   * @param email correo del usuario
   */
  resetPassword(email: any): Promise<void> {
    return runInInjectionContext(this.injector, () => this.afAuth.sendPasswordResetEmail(email));
  }


  /**
   * Crea el usuario en la BD
   *
   * @param user info del usuario
   */
  setDataUser(user: UserSign): void {
    const userData: UserSign = {
      uid: user.uid,
      email: user.email,
      displayName: (user.displayName !== null) ? user.displayName : 'Sin información',
      photoURL: user.photoURL,
      emailVerified: user.emailVerified
    };
    this.db.set(`${COLLECTIONS_BD.admins}/${user.uid}`, userData);
  }

  /**
   * Actualiza el usuario en la BD
   *
   * @param user info del usuario
   */
  updateDataUser(user: UserSign): void {
    const userData: UserSign = {
      uid: user.uid,
      email: user.email,
      photoURL: user.photoURL,
      emailVerified: user.emailVerified,
    };
    this.db.update(COLLECTIONS_BD.admins, userData.uid, userData);
  }
}
